import { useState } from "react";
import { ShieldCheck, Mic, Trash2, FileText, Lock } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// "What happens to my voice?" — tap-to-open explanation next to every microphone.
// variant "dictation": audio is transcribed and discarded (never stored).
// variant "voiceMessage": a deliberate voice note to the coach IS stored until deleted.
export function VoicePrivacyHint({ variant = "dictation", tone = "light", compact = false, className = "", testId = "voice-privacy-hint" }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const stored = variant === "voiceMessage";
  const pillTone = tone === "dark"
    ? "bg-white/15 text-white hover:bg-white/25"
    : "bg-[#5A8B8F]/10 text-[#2C5254] dark:bg-white/10 dark:text-teal-200 hover:bg-[#5A8B8F]/20";
  const items = stored
    ? [
        { icon: Mic, text: t("voicePrivacy.msg1") },
        { icon: Lock, text: t("voicePrivacy.msg2") },
        { icon: Trash2, text: t("voicePrivacy.msg3") },
      ]
    : [
        { icon: FileText, text: t("voicePrivacy.dict1") },
        { icon: Trash2, text: t("voicePrivacy.dict2") },
        { icon: Lock, text: t("voicePrivacy.dict3") },
      ];
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={testId}
        aria-label={t("voicePrivacy.title")}
        title={t("voicePrivacy.title")}
        className={`inline-flex items-center gap-1 rounded-full font-medium transition active:scale-95 ${pillTone} ${
          compact ? "h-7 w-7 justify-center" : "px-2.5 py-1 text-[11px]"
        } ${className}`}
      >
        <ShieldCheck className={compact ? "h-3.5 w-3.5" : "h-3 w-3"} />
        {!compact && <span>{stored ? t("voicePrivacy.pillStored") : t("voicePrivacy.pillNotStored")}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`${testId}-dialog`} onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-5 w-5 text-[#5A8B8F]" /> {t("voicePrivacy.title")}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {stored ? t("voicePrivacy.introStored") : t("voicePrivacy.introDictation")}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
            {items.map(({ icon: Icon, text }, i) => (
              <li key={i} className="flex gap-3">
                <span className="h-8 w-8 shrink-0 rounded-full bg-[#5A8B8F]/10 text-[#2C5254] dark:text-teal-200 flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="leading-snug">{text}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-700 pt-3">
            {t("voicePrivacy.never")}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default VoicePrivacyHint;
